import { useMemo, useState } from 'react'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  FinanceCreditNoteSequencesSelectorDocument,
  FinanceSalesInvoiceSequencesSelectorDocument,
} from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import LabeledInput from 'modules/core/components/LabeledInput'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import i18n from 'modules/core/lib/i18n'

import { SequenceType as CreateSequenceType } from 'modules/finance/activities/serialSequenceCreate/SerialSequenceModal'

import { mapCreditNoteSequences, mapSalesInvoiceSequences, toBaseSerialSequence } from './mappers'
import { GroupType, SerialSequenceSelectorProps } from './types'

export const SerialSequenceSelector = (props: SerialSequenceSelectorProps) => {
  const { openActivity } = useActivities()
  const isCreditNote = props.type === 'credit_note'

  const openCreateSerialSequenceActivity = (search?: string) => {
    const sequenceType: CreateSequenceType = isCreditNote ? 'credit_note' : 'invoice'
    openActivity('finance.serialSequenceCreate', {
      refetchQuery: 'SerialSequencesCollection',
      name: search,
      legalEntityId: props.queryParams.legalEntityId?.toString(),
      sequenceType,
    })
  }
  // We skip because we only want to fetch when opening the first time the selector (if we are alwaysopen then we fetch from the very beginning)
  const [skip, setSkip] = useState<boolean>(!(props.alwaysOpen || props.defaultOpen))

  const { loading: loadingInvoices, data: invoiceData } = useQuery(
    FinanceSalesInvoiceSequencesSelectorDocument,
    {
      variables: {
        status: props.queryParams.status,
        legalEntityId: props.queryParams.legalEntityId,
      },
      skip: skip || isCreditNote,
    }
  )

  const { loading: loadingCreditNotes, data: creditNoteData } = useQuery(
    FinanceCreditNoteSequencesSelectorDocument,
    {
      variables: {
        status: props.queryParams.status,
        legalEntityId: props.queryParams.legalEntityId,
      },
      skip: skip || !isCreditNote,
    }
  )

  const loading = isCreditNote ? loadingCreditNotes : loadingInvoices
  const data = isCreditNote ? creditNoteData : invoiceData

  const canCreate = data?.finance._policies.financialSequencesManage.can

  const onOpen = () => {
    if (skip) setSkip(false)
  }

  const serialSequencesEntities = useMemo(() => {
    if (isCreditNote) {
      return mapCreditNoteSequences(creditNoteData?.finance.creditNoteSequences ?? [])
    }
    return mapSalesInvoiceSequences(invoiceData?.finance.salesInvoiceSequences ?? [])
  }, [
    isCreditNote,
    invoiceData?.finance.salesInvoiceSequences,
    creditNoteData?.finance.creditNoteSequences,
  ])

  const selectedSerialSequences = useMemo(
    () =>
      props.value
        ? [
            {
              id: props.value.id,
              name: props.value.name + ' -> ' + props.value.humanReadablePreview,
            },
          ]
        : [],
    [props.value]
  )

  const groupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: serialSequencesEntities,
    }),
    [serialSequencesEntities]
  )

  const selectedGroupMap: Record<GroupType, EntitySelectEntity[]> = useMemo(
    () => ({
      all: selectedSerialSequences ?? [],
    }),
    [selectedSerialSequences]
  )

  const handleOnSelectSingle = (selection: EntitySelectEntity | null) => {
    if (!selection) {
      props.onSelect(null)
      return
    }

    // Find the original fragment from the query data to preserve all fields
    const originalSequence = isCreditNote
      ? creditNoteData?.finance.creditNoteSequences.find((s) => s.id === selection.id)
      : invoiceData?.finance.salesInvoiceSequences.find((s) => s.id === selection.id)

    if (originalSequence) {
      props.onSelect(toBaseSerialSequence(originalSequence))
    }
  }

  const firstSequence = selectedSerialSequences[0]
  const name = !!props.value && firstSequence ? firstSequence.name : undefined

  return (
    <Box
      minWidth={props.minWidth}
      width={props.width ?? props.minWidth ?? (props.children ? 'fit-content' : 'full')}
      position="relative"
    >
      <EntitySelectWrapper
        label={i18n.t('finance.serial_sequences.selector.select_sequences')}
        hideLabel
        hiddenAvatar
        loading={loading}
        onOpen={onOpen}
        entities={groupMap['all'] || []}
        selectedGroup={'all'}
        selectedGroupedEntities={selectedGroupMap['all']}
        onSelectExpandedElements={() => {}}
        expandedElements={[]}
        selectedEntities={selectedSerialSequences}
        searchPlaceholder={i18n.t('finance.serial_sequences.selector.search')}
        selectAllLabel={i18n.t('finance.serial_sequences.selector.select_all')}
        clearLabel={i18n.t('finance.serial_sequences.selector.clear')}
        selectedLabel={i18n.t('finance.serial_sequences.selector.selected')}
        notFoundSubtitle={i18n.t('finance.serial_sequences.selector.not_found.subtitle')}
        notFoundTitle={i18n.t('finance.serial_sequences.selector.not_found.title')}
        placeholder={i18n.t('finance.serial_sequences.selector.select_sequences')}
        selectedItemsCopy={i18n.t('finance.serial_sequences.selector.sequences_selected')}
        groups={[{ label: i18n.t('finance.serial_sequences.selector.groups.none'), value: 'all' }]}
        onGroupChange={() => {}}
        onSelectMultiple={() => {}}
        singleSelector
        onSelect={handleOnSelectSingle}
        minWidth={props.minWidth}
        width={props.width}
        onCreate={canCreate ? openCreateSerialSequenceActivity : undefined}
        onCreateLabel={i18n.t('finance.serial_sequences.selector.create_new_sequence')}
      >
        {props.children ? (
          props.children
        ) : (
          <Box flexDirection="row" width="full">
            <LabeledInput
              aria-label="serial-sequence-selector"
              readonly={props.disabled}
              error={props.error}
              mandatory={props.mandatory}
              label={props.label}
              value={props.value?.toString() ?? undefined}
            >
              <Box
                paddingX="s16"
                paddingY="s8"
                flexDirection="row"
                height="s40"
                alignItems="center"
                width="full"
                cursor={props.disabled ? 'default' : 'pointer'}
              >
                <Box flexDirection="row" width="full" gap="s6">
                  {!props.hideSearchIcon && (
                    <Icon.Medium icon="search" color={props.disabled ? 'grey600' : 'grey800'} />
                  )}
                  <Box overflowX="hidden" textAlign="left">
                    <Text.Normal ellipsis>
                      {name ??
                        props.emptyLabel ??
                        i18n.t('finance.serial_sequences.selector.empty_label')}
                    </Text.Normal>
                  </Box>
                </Box>
                <Icon.Small icon="caret-down" />
              </Box>
            </LabeledInput>
          </Box>
        )}
      </EntitySelectWrapper>
    </Box>
  )
}
